// Draws the share card to a canvas for download — the same layout as the DOM
// card, in 300×400 "card units" scaled up. Kept as plain canvas drawing (not a
// DOM rasterizer) so gradient-tinted vectors and cross-origin images behave
// predictably.

import { TINT_ANGLE, TINT_WASH } from './shareTints';
import { drawQr } from './qr';

export const CARD_W = 300;
export const CARD_H = 400;

// The DOM card's geometry, mirrored here.
export const LAYOUT = {
    logo: { x: 102, y: 26, w: 96, h: 15 },
    // The subject image (avatar or community banner), centered.
    subject: { cx: 150, cy: 96, size: 80 },
    name: { y: 160 },
    caption: { y: 178 },
    // `mark` is the logo's side; `excavate` how many modules are cleared for it.
    qr: { x: 86, y: 196, size: 128, mark: 24, excavate: 7 },
    link: { y: 376 },
};

// Corner radius of the squircle subject, as a fraction of its side — the same
// 23% `.community-banner-block` wears.
const SQUIRCLE = 0.23;

const FONT = '"League Spartan", sans-serif';

function loadImage(src, crossOrigin = false) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        if (crossOrigin) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        // The on-screen <img> already cached this URL *without* CORS headers;
        // a CORS-mode load served from that cache entry fails, so bust it.
        img.src = crossOrigin ? `${src}${src.includes('?') ? '&' : '?'}cors=1` : src;
    });
}

function parseColor(color) {
    const hex = /^#([0-9a-f]{6})$/i.exec(color?.trim() || '');
    if (hex) {
        const n = parseInt(hex[1], 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    const rgb = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i.exec(color || '');
    if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    return [255, 255, 255];
}

function mix(a, b, t) {
    const [r1, g1, b1] = parseColor(a);
    const [r2, g2, b2] = parseColor(b);
    const ch = (x, y) => Math.round(x + (y - x) * t);
    return `rgb(${ch(r1, r2)}, ${ch(g1, g2)}, ${ch(b1, b2)})`;
}

// Linear gradient across a box at TINT_ANGLE (CSS angle convention).
function gradientFor(ctx, x, y, w, h, stops) {
    const rad = ((TINT_ANGLE - 90) * Math.PI) / 180;
    const half = (Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad))) / 2;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const g = ctx.createLinearGradient(
        cx - Math.cos(rad) * half, cy - Math.sin(rad) * half,
        cx + Math.cos(rad) * half, cy + Math.sin(rad) * half,
    );
    g.addColorStop(0, stops[0]);
    g.addColorStop(1, stops[1]);
    return g;
}

// Re-colors an opaque-on-transparent source (logo, QR) with a solid or gradient
// fill via source-in compositing, at the destination's pixel size.
function tinted(source, pxW, pxH, makeFill) {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(pxW));
    c.height = Math.max(1, Math.round(pxH));
    const g = c.getContext('2d');
    g.drawImage(source, 0, 0, c.width, c.height);
    g.globalCompositeOperation = 'source-in';
    g.fillStyle = makeFill(g, c.width, c.height);
    g.fillRect(0, 0, c.width, c.height);
    return c;
}

export function initials(name = '') {
    return name.trim().split(/\s+/).slice(0, 2).map(p => p[0] || '').join('').toUpperCase();
}

function fitText(ctx, text, maxWidth) {
    if (ctx.measureText(text).width <= maxWidth) return text;
    let s = text;
    while (s.length > 1 && ctx.measureText(`${s}…`).width > maxWidth) s = s.slice(0, -1);
    return `${s}…`;
}

// Traces the subject's outline — a circle for people, the 23% squircle for
// communities — so both the well and the image itself wear the DOM's shape.
function subjectPath(ctx, shape, x, y, size) {
    ctx.beginPath();
    if (shape !== 'squircle') {
        ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        return;
    }
    const r = size * SQUIRCLE;
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + size, y, x + size, y + size, r);
    ctx.arcTo(x + size, y + size, x, y + size, r);
    ctx.arcTo(x, y + size, x, y, r);
    ctx.arcTo(x, y, x + size, y, r);
    ctx.closePath();
}

/**
 * @param {object} opts
 * @param {{name:string, caption?:string, image?:string, initials?:string, shape?:'circle'|'squircle'}} opts.card
 * @param {string} opts.url            full destination URL (QR payload)
 * @param {string} opts.displayUrl     URL as printed on the card
 * @param {{stops:string[]|null}} opts.tint
 * @param {{size:number, modules:boolean[][], finders:object[]}} opts.qr   from buildQr()
 * @param {string} opts.logoFullUrl
 * @param {string} opts.logoSquareUrl
 * @param {{surface:string, ink:string, sub:string, soft:string, well:string}} opts.colors  resolved CSS colors
 * @param {number} [opts.scale]        output scale (4 → 1200×1600)
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function renderShareCard({ card, url, displayUrl, tint, qr, logoFullUrl, logoSquareUrl, colors, scale = 4 }) {
    if (document.fonts?.ready) await document.fonts.ready;

    const canvas = document.createElement('canvas');
    canvas.width = CARD_W * scale;
    canvas.height = CARD_H * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);

    const stops = tint?.stops || null;
    // Fill used to re-color the vectors (logo, QR, mark): the tint gradient
    // across the vector's own box, or the ink color for the neutral scheme.
    const fillFor = () => (g, pw, ph) => (
        stops ? gradientFor(g, 0, 0, pw, ph, stops) : colors.ink
    );

    // ── Card fill (surface, optionally washed with the tint) ────────────────
    ctx.fillStyle = colors.surface;
    ctx.fillRect(0, 0, CARD_W, CARD_H);
    if (stops) {
        ctx.fillStyle = gradientFor(ctx, 0, 0, CARD_W, CARD_H, [
            mix(colors.surface, stops[0], TINT_WASH),
            mix(colors.surface, stops[1], TINT_WASH),
        ]);
        ctx.fillRect(0, 0, CARD_W, CARD_H);
    }

    // ── Logo ────────────────────────────────────────────────────────────────
    const [logoFull, logoSquare] = await Promise.all([loadImage(logoFullUrl), loadImage(logoSquareUrl)]);
    const L = LAYOUT.logo;
    ctx.drawImage(tinted(logoFull, L.w * scale, L.h * scale, fillFor()), L.x, L.y, L.w, L.h);

    // ── Subject image (cross-origin may refuse; fall back to initials) ──────
    const S = LAYOUT.subject;
    const sx = S.cx - S.size / 2;
    const sy = S.cy - S.size / 2;
    ctx.save();
    subjectPath(ctx, card.shape, sx, sy, S.size);
    ctx.fillStyle = colors.well;
    ctx.fill();
    ctx.clip();
    let drewImage = false;
    if (card.image) {
        try {
            const img = await loadImage(card.image, true);
            const side = Math.min(img.width, img.height);
            ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, sx, sy, S.size, S.size);
            drewImage = true;
        } catch (e) {
            drewImage = false;
        }
    }
    if (!drewImage) {
        ctx.fillStyle = colors.sub;
        ctx.font = `600 26px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(card.initials || initials(card.name), S.cx, S.cy + 1);
    }
    ctx.restore();

    // ── Name + caption ──────────────────────────────────────────────────────
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = colors.ink;
    ctx.font = `500 18px ${FONT}`;
    ctx.fillText(fitText(ctx, card.name || '', CARD_W - 48), CARD_W / 2, LAYOUT.name.y);
    if (card.caption) {
        ctx.fillStyle = colors.soft;
        ctx.font = `400 12px ${FONT}`;
        ctx.fillText(fitText(ctx, card.caption, CARD_W - 48), CARD_W / 2, LAYOUT.caption.y);
    }

    // ── QR + center mark ────────────────────────────────────────────────────
    // The rounded code is drawn black on an offscreen canvas, then re-colored
    // with the same fill as the logo so gradients span the whole code.
    const Q = LAYOUT.qr;
    const qrSource = document.createElement('canvas');
    qrSource.width = Q.size * scale;
    qrSource.height = Q.size * scale;
    drawQr(qrSource.getContext('2d'), qr, Q.size * scale, '#000000');
    ctx.drawImage(tinted(qrSource, Q.size * scale, Q.size * scale, fillFor()), Q.x, Q.y, Q.size, Q.size);
    const mx = Q.x + Q.size / 2 - Q.mark / 2;
    const my = Q.y + Q.size / 2 - Q.mark / 2;
    ctx.drawImage(tinted(logoSquare, Q.mark * scale, Q.mark * scale, fillFor()), mx, my, Q.mark, Q.mark);

    // ── Link ────────────────────────────────────────────────────────────────
    ctx.fillStyle = colors.sub;
    ctx.font = `400 11px ${FONT}`;
    ctx.fillText(fitText(ctx, displayUrl || url, CARD_W - 48), CARD_W / 2, LAYOUT.link.y);

    return canvas;
}

export function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/png');
    });
}
